import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Field,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';
import UploadsPanel from '../components/UploadsPanel';
import GlMapEditor from '../components/GlMapEditor';
import Instructions from '../components/Instructions';
import WorkTab from '../components/WorkTab';
import {
  ExportError,
  useClientXeroTenant,
  useConversions,
  useJournals,
  useDownloadConversion,
} from '../services/conversionsApi';
import { CONVERSION_FLOWS } from '../types';
import { clientRecordsFreshness, formatLocalDateTime } from '../lib/provenance';
import { activeXeroConfirmation } from '../lib/xeroImport';
import {
  describePeriod,
  describeTotals,
  formatDateTime,
  isDownloadable,
  saveDownloadedFile,
} from '../lib/conversionHistory';
import type { ConversionFlow, ConversionHistoryFilters, ConversionRecord, XeroTenantProvenance } from '../types';
import { dr15JournalHandoffUrl } from '../lib/journalHandoff';
import { CalendarDateInput } from '../components/dates/CalendarDateInput';
import { usePageStyles } from '../components/layout/pageStyles';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  filters: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'flex-end' },
  narrow: { width: '190px' },
  numeric: { textAlign: 'right' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  xeroRow: { display: 'flex', gap: tokens.spacingHorizontalL, alignItems: 'baseline' },
  xeroLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    minWidth: '150px',
  },
  xeroValue: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

const TABS = ['work', 'conversions', 'documents', 'glMapping'] as const;
type TabValue = (typeof TABS)[number];

/**
 * The per-client page (CONVERSIONS_SPEC.md §8.2) — pick a client, then four tabs, Work first (docs/work-history.md §2).
 *
 * <p>This is the screen C6's backend was built for. C6 shipped the whole history layer
 * and, once the recording was wired, still had nothing that could show it: the endpoints
 * had no caller. Until this page existed, a retained conversion was write-only.</p>
 *
 * <p>⚠ <b>The client id lives in the URL (`?clientId=…`), not in component state.</b>
 * §9's rule is one client context per page, linkable and refresh-surviving — a bookkeeper
 * sending "look at this client's history" to a colleague should be sending a URL, not
 * instructions for reproducing a click path.</p>
 */
export default function ClientPage() {
  const frame = usePageStyles();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';
  // The tab is `?tab=` (the fleet view-state rule), so Back from a journal opened in Work returns to Work. Replaced, not
  // pushed: switching tabs is looking around, not a step Back should retrace one by one.
  const tabParam = searchParams.get('tab');
  const tab: TabValue = (TABS as readonly string[]).includes(tabParam ?? '') ? (tabParam as TabValue) : 'work';
  const setTab = (next: TabValue) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  // The client is picked in the HEADER now (docs/client-context.md): one picker, every screen.

  return (
    <div className={frame.page}>
      <div className={frame.header}>
        <Text as="h1" className={frame.title}>{t('clientPage.title')}</Text>
        <Text className={frame.subtitle}>{t('clientPage.subtitle')}</Text>
      </div>

      {!clientId ? (
        <MessageBar intent="info">
          <MessageBarBody>{t('clientPage.pickAClient')}</MessageBarBody>
        </MessageBar>
      ) : (
        <>
          <XeroOrganisationRow clientId={clientId} />

          <TabList
            selectedValue={tab}
            onTabSelect={(_e, data) => setTab(data.value as TabValue)}
          >
            <Tab value="work">{t('clientPage.tabs.work')}</Tab>
            <Tab value="conversions">{t('clientPage.tabs.conversions')}</Tab>
            <Tab value="documents">{t('clientPage.tabs.documents')}</Tab>
            <Tab value="glMapping">{t('clientPage.tabs.glMapping')}</Tab>
          </TabList>

          {/*
            §6 mounts the panel PER TAB, not once on the page: the three tabs are three
            different tasks, and a single panel above the TabList would have to describe
            all three at once — which is the undifferentiated column §9 broke ExportPage
            apart to escape. Each panel is dismissed independently, per §6.
          */}
          {tab === 'work' && (
            <>
              <Instructions screen="work" />
              <WorkTab clientId={clientId} />
            </>
          )}
          {tab === 'conversions' && (
            <>
              <Instructions screen="clientHistory" />
              <ConversionsTab clientId={clientId} />
            </>
          )}
          {/*
            §9: "Uploads are shared, not owned by a flow." The same panel the two bank-flow
            screens use, in `library` mode — the document list with NO conversion buttons,
            because this tab is the client's document library rather than a conversion
            surface. The page already shows a client picker, so the panel does not.
          */}
          {tab === 'documents' && <Instructions screen="uploads" />}
          {tab === 'documents' && (
            <UploadsPanel
              clientId={clientId}
              mode="library"
              // programmatic-nav: after the extraction, the journal it fills (no element is being followed).
              onDr15Extracted={(doc, result) => navigate(dr15JournalHandoffUrl(doc, result))}
            />
          )}
          {/*
            §8.2: the role table inline. Same editor as the standalone G/L Mappings screen
            — one implementation, because two editable forms over the same records would
            drift and staff could not tell which they were looking at.
          */}
          {tab === 'glMapping' && (
            <>
              {/* Same `glMappings` copy — and therefore the same dismissal — as the
                  standalone screen. They are one task reached two ways, so a bookkeeper
                  who has dismissed these steps should not meet them again in a tab. */}
              <Instructions screen="glMappings" />
              <GlMapEditor clientId={clientId} />
            </>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The client's linked Xero organisation (C8).
 *
 * <p>⚠ <b>Unlinked is the PRIMARY state and renders as ordinary information.</b>
 * `core.XeroTenant` is empty in production, so every client reads "Not linked" today.
 * An empty registry is not a fault: a warning icon on every client would train staff
 * to ignore this row before the first real link ever exists.</p>
 *
 * <p>A read FAILURE is a different claim and does get a warning. "The link could not
 * be read" is not "there is no link" — collapsing the two would state as fact
 * something nobody measured, which is how a client with a real Xero organisation
 * would silently read as unlinked during an outage.</p>
 */
function XeroOrganisationRow({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = useClientXeroTenant(clientId);
  const link = data?.link ?? null;
  const provenance = data?.provenance ?? null;

  if (isLoading) {
    return <Text className={styles.muted}>{t('clientPage.xero.loading')}</Text>;
  }

  if (isError) {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>{t('clientPage.xero.readFailed')}</MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <div className={styles.xeroRow}>
      <Text className={styles.xeroLabel}>{t('clientPage.xero.label')}</Text>
      <div className={styles.xeroValue}>
        {link ? (
          <>
            <Text>{link.tenantId}</Text>
            {(link.tenantType || link.status) && (
              <Text className={styles.muted}>
                {[link.tenantType, link.status].filter(Boolean).join(' · ')}
              </Text>
            )}
          </>
        ) : (
          <>
            <Text>{t('clientPage.xero.notLinked')}</Text>
            <Text className={styles.muted}>{t('clientPage.xero.notLinkedHint')}</Text>
          </>
        )}
        {provenance && <XeroProvenance provenance={provenance} linked={!!link} locale={i18n.language} />}
        <LatestJournalInXero clientId={clientId} />
      </div>
    </div>
  );
}

/**
 * B2 (docs/core-workflow.md): whether the client's latest EXPORTED journal is in Xero, where the Xero link is. An
 * export a person has not confirmed reads "not yet confirmed": an export can never know it landed.
 */
function LatestJournalInXero({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const journals = useJournals(clientId);
  const latest = (journals.data ?? [])
    .filter((j) => j.status === 'exported' && !j.supersededByJournalId)
    .sort((a, b) => b.journalDate.localeCompare(a.journalDate))[0];
  if (!latest) return null;

  const active = activeXeroConfirmation(latest);
  return (
    <Text className={styles.muted}>
      {active
        ? t('clientPage.xero.latestConfirmed', { date: latest.journalDate, name: active.confirmedByName ?? t('uploads.removed.someone') })
        : t('clientPage.xero.latestUnconfirmed', { date: latest.journalDate })}
    </Text>
  );
}

/**
 * CNV-004: when the row was checked, and how old ptc-core's client records behind it are. Only an OVERDUE refresh
 * on an UNLINKED client warns: a stale mirror can make a client read "Not linked" wrongly, but it cannot invent a
 * link, so a linked answer needs no caveat. An unknown refresh time is a quiet note, not a warning, for the same
 * reason "Not linked" is not one: a warning on every row trains staff to ignore it.
 */
function XeroProvenance({
  provenance,
  linked,
  locale,
}: {
  provenance: XeroTenantProvenance;
  linked: boolean;
  locale: string;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const freshness = clientRecordsFreshness(provenance.clientRecordsRefreshedAtUtc, new Date());
  const checked = formatLocalDateTime(provenance.checkedAtUtc, locale);
  const refreshed = provenance.clientRecordsRefreshedAtUtc
    ? formatLocalDateTime(provenance.clientRecordsRefreshedAtUtc, locale)
    : '';

  return (
    <>
      <Text className={styles.muted}>
        {freshness === 'unknown'
          ? t('clientPage.xero.provenanceUnknown', { checked })
          : t('clientPage.xero.provenance', { checked, refreshed })}
      </Text>
      {!linked && freshness === 'overdue' && (
        <MessageBar intent="warning">
          <MessageBarBody>{t('clientPage.xero.recordsOverdue', { refreshed })}</MessageBarBody>
        </MessageBar>
      )}
    </>
  );
}

function ConversionsTab({ clientId }: { clientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [flow, setFlow] = useState<'' | ConversionFlow>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filters = useMemo<ConversionHistoryFilters>(
    () => ({
      ...(flow ? { flow } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [flow, from, to],
  );

  const query = useConversions(clientId, filters);
  const download = useDownloadConversion();
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // The server rejects an inverted range with a named 400, but saying so before the round
  // trip is cheaper and clearer than round-tripping to be told.
  const rangeInverted = !!from && !!to && from > to;

  const onDownload = async (record: ConversionRecord) => {
    setDownloadError(null);
    try {
      saveDownloadedFile(await download.mutateAsync({ clientId, conversionId: record.id }));
    } catch (err) {
      // The server's own message is surfaced verbatim: it distinguishes "no such
      // conversion" from "recorded but not retained", and those send staff to different
      // places. A generic "download failed" would erase that.
      setDownloadError(err instanceof ExportError ? err.message : t('clientPage.conversions.downloadFailed'));
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.filters}>
        <Field label={t('clientPage.conversions.filterFlow')} className={styles.narrow}>
          <Select value={flow} onChange={(_e, data) => setFlow(data.value as '' | ConversionFlow)}>
            <option value="">{t('clientPage.conversions.allFlows')}</option>
            {CONVERSION_FLOWS.map((f) => (
              <option key={f} value={f}>{t(`clientPage.conversions.flow.${f}`)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('clientPage.conversions.filterFrom')} className={styles.narrow}>
          <CalendarDateInput value={from} onChange={setFrom} />
        </Field>
        <Field label={t('clientPage.conversions.filterTo')} className={styles.narrow}>
          <CalendarDateInput value={to} onChange={setTo} />
        </Field>
      </div>

      {/* Both dates bound generatedAt — when the conversion was RUN, not the period it
          covers. Those are different questions and the screen says which one it filters. */}
      <Text className={styles.muted}>{t('clientPage.conversions.filterNote')}</Text>

      {rangeInverted && (
        <MessageBar intent="warning">
          <MessageBarBody>{t('clientPage.conversions.rangeInverted')}</MessageBarBody>
        </MessageBar>
      )}

      {downloadError && (
        <MessageBar intent="error">
          <MessageBarBody>{downloadError}</MessageBarBody>
        </MessageBar>
      )}

      {query.isLoading && <Spinner label={t('common.loading')} />}

      {query.isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {query.error instanceof Error ? query.error.message : t('clientPage.conversions.loadFailed')}
          </MessageBarBody>
        </MessageBar>
      )}

      {query.data && query.data.length === 0 && (
        <div className={styles.empty}>
          <Text>{t('clientPage.conversions.empty')}</Text>
          {/* ⚠ Deliberately not phrased as "this client has never had a conversion". The
              history is fail-open and therefore best-effort: a missing row means "we did
              not record it", never "it did not happen". The screen must not let a
              bookkeeper prove a negative it cannot support. */}
          <br />
          <Text className={styles.muted}>{t('clientPage.conversions.emptyCaveat')}</Text>
        </div>
      )}

      {query.data && query.data.length > 0 && (
        <Table aria-label={t('clientPage.conversions.tableLabel')}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('clientPage.conversions.colGenerated')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.conversions.colFlow')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.conversions.colFile')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.conversions.colPeriod')}</TableHeaderCell>
              <TableHeaderCell className={styles.numeric}>
                {t('clientPage.conversions.colTotals')}
              </TableHeaderCell>
              <TableHeaderCell>{t('clientPage.conversions.colWho')}</TableHeaderCell>
              <TableHeaderCell>{t('clientPage.conversions.colDownload')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.data.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{formatDateTime(record.generatedAt)}</TableCell>
                <TableCell>
                  <Badge appearance="tint">{t(`clientPage.conversions.flow.${record.flow}`)}</Badge>
                </TableCell>
                <TableCell>
                  {record.fileName}
                  {record.summary && (
                    <>
                      <br />
                      <Text className={styles.muted}>{record.summary}</Text>
                    </>
                  )}
                </TableCell>
                <TableCell>{describePeriod(record)}</TableCell>
                <TableCell className={styles.numeric}>{describeTotals(record)}</TableCell>
                <TableCell>{record.generatedByName ?? '—'}</TableCell>
                <TableCell>
                  {isDownloadable(record) ? (
                    <Button
                      appearance="subtle"
                      icon={<ArrowDownloadRegular />}
                      disabled={download.isPending}
                      onClick={() => void onDownload(record)}
                    >
                      {t('clientPage.conversions.download')}
                    </Button>
                  ) : (
                    // No button at all, rather than one that 404s. "Recorded but not
                    // retained" is a real outcome and is named as such.
                    <Text className={styles.muted}>{t('clientPage.conversions.notRetained')}</Text>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
